import React, { useState, useEffect } from 'react';
import { FaXmark, FaFloppyDisk, FaPlus, FaTrash } from 'react-icons/fa6';

interface SettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const SettingsModal: React.FC<SettingsModalProps> = ({ isOpen, onClose }) => {
  const [emailRecipients, setEmailRecipients] = useState<string[]>([]);
  const [newEmail, setNewEmail] = useState('');
  const [loading, setLoading] = useState(false);

  const [saving, setSaving] = useState(false);
  const [testingSending, setTestingSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Load current settings
  useEffect(() => {
    if (isOpen) {
      loadSettings();
    }
  }, [isOpen]);

  const loadSettings = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch('/api/subcontractor/settings/email_notification_recipients', {
        credentials: 'include',
      });
      
      if (!response.ok) {
        throw new Error('Failed to load settings');
      }
      
      const data = await response.json();
      if (data.value && Array.isArray(data.value)) {
        setEmailRecipients(data.value);
      }
    } catch (err: any) {
      console.error('[Settings] Error loading settings:', err);
      setError(err.message || 'Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    setError(null);
    setSuccessMessage(null);
    
    try {
      const response = await fetch('/api/subcontractor/settings/email_notification_recipients', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ value: emailRecipients }),
      });
      
      if (!response.ok) {
        throw new Error('Failed to save settings');
      }
      
      setSuccessMessage('Settings saved successfully!');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err: any) {
      console.error('[Settings] Error saving settings:', err);
      setError(err.message || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const addEmail = () => {
    const trimmedEmail = newEmail.trim();
    if (!trimmedEmail) return;
    
    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(trimmedEmail)) {
      setError('Please enter a valid email address');
      return;
    }
    
    if (emailRecipients.includes(trimmedEmail)) {
      setError('This email is already in the list');
      return;
    }
    
    setEmailRecipients([...emailRecipients, trimmedEmail]);
    setNewEmail('');
    setError(null);
  };

  const removeEmail = (email: string) => {
    setEmailRecipients(emailRecipients.filter(e => e !== email));
  };

  const sendTestEmail = async () => {
    if (emailRecipients.length === 0) {
      setError('Please add at least one email recipient');
      return;
    }

    setTestingSending(true);
    setError(null);
    setSuccessMessage(null);
    
    try {
      const response = await fetch('/api/subcontractor/settings/test-email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ recipients: emailRecipients }),
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Failed to send test email' }));
        throw new Error(errorData.error || 'Failed to send test email');
      }
      
      const result = await response.json();
      setSuccessMessage(result.message || 'Test notification sent successfully! Check your inbox for an expiring attachment alert.');
      setTimeout(() => setSuccessMessage(null), 8000);
    } catch (err: any) {
      console.error('[Settings] Error sending test email:', err);
      setError(err.message || 'Failed to send test email');
    } finally {
      setTestingSending(false);
    }
  };
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addEmail();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content settings-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Settings</h2>
          <button className="close-btn" onClick={onClose} title="Close">
            <FaXmark />
          </button>
        </div>

        <div className="modal-body">
          {loading ? (
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <div className="spinner"></div>
              <p>Loading settings...</p>
            </div>
          ) : (
            <>
              <section className="settings-section">
                <h3>Email Notification Recipients</h3>
                <p className="settings-description">
                  These email addresses will receive notifications when attachments are approaching their expiration dates (90 days prior).
                </p>

                <div className="email-list">
                  {emailRecipients.length === 0 ? (
                    <div className="empty-state">No recipients configured</div>
                  ) : (
                    emailRecipients.map((email, index) => (
                      <div key={index} className="email-item">
                        <span className="email-address">{email}</span>
                        <button
                          className="remove-btn"
                          onClick={() => removeEmail(email)}
                          title="Remove"
                        >
                          <FaTrash />
                        </button>
                      </div>
                    ))
                  )}
                </div>

                <div className="add-email-form">
                  <input
                    type="email"
                    className="email-input"
                    placeholder="Enter email address"
                    value={newEmail}
                    onChange={(e) => setNewEmail(e.target.value)}
                    onKeyPress={handleKeyPress}
                  />
                  <button className="add-btn" onClick={addEmail} title="Add email">
                    <FaPlus /> Add
                  </button>
                </div>
              </section>

              {error && (
                <div className="error-message">
                  {error}
                </div>
              )}

              {successMessage && (
                <div className="success-message">
                  {successMessage}
                </div>
              )}
            </>
          )}
        </div>

        <div className="modal-footer">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button 
            className="btn-test" 
            onClick={sendTestEmail} 
            disabled={testingSending || loading || emailRecipients.length === 0}
            title="Send a sample expiring attachment notification email to test the system"
          >
            {testingSending ? (
              <>
                <div className="spinner-small"></div> Sending...
              </>
            ) : (
              'Send Test Email'
            )}
          </button>
          <button 
            className="btn-primary" 
            onClick={saveSettings} 
            disabled={saving || loading}
          >
            {saving ? (
              <>
                <div className="spinner-small"></div> Saving...
              </>
            ) : (
              <>
                <FaFloppyDisk /> Save Settings
              </>
            )}
          </button>
        </div>
      </div>

      <style>{`
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.75);
          backdrop-filter: blur(4px);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 9999;
          padding: 20px;
        }

        .modal-content {
          background: #181818;
          border: 1px solid #333;
          border-radius: 12px;
          box-shadow: 0 10px 40px rgba(0, 0, 0, 0.8);
          display: flex;
          flex-direction: column;
          color: #ddd;
          max-height: 90vh;
          width: 100%;
        }

        .settings-modal {
          max-width: 600px;
        }

        .modal-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 16px 20px;
          border-bottom: 1px solid #333;
        }

        .modal-header h2 {
          margin: 0;
          font-size: 18px;
          font-weight: 600;
          color: #fff;
        }

        .close-btn {
          background: none;
          border: none;
          color: #888;
          cursor: pointer;
          font-size: 20px;
          padding: 4px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 4px;
          transition: color 0.2s, background-color 0.2s;
        }

        .close-btn:hover {
          color: #fff;
          background: #262626;
        }

        .modal-body {
          padding: 20px;
          overflow-y: auto;
          flex: 1;
          min-height: 0;
        }

        .modal-footer {
          display: flex;
          gap: 10px;
          padding: 16px 20px;
          border-top: 1px solid #333;
          justify-content: flex-end;
        }

        .settings-section {
          margin-bottom: 24px;
        }

        .settings-section:last-child {
          margin-bottom: 0;
        }

        .settings-section h3 {
          margin: 0 0 8px 0;
          font-size: 15px;
          color: #fff;
          font-weight: 600;
        }

        .settings-description {
          margin: 0 0 16px 0;
          font-size: 13px;
          color: #999;
          line-height: 1.5;
        }

        .email-list {
          background: #202020;
          border: 1px solid #333;
          border-radius: 8px;
          padding: 12px;
          margin-bottom: 16px;
          min-height: 60px;
          max-height: 200px;
          overflow-y: auto;
        }

        .empty-state {
          text-align: center;
          padding: 20px;
          color: #555;
          font-size: 13px;
        }

        .email-item {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 10px 12px;
          margin-bottom: 8px;
          background: #262626;
          border: 1px solid #333;
          border-radius: 6px;
        }

        .email-item:last-child {
          margin-bottom: 0;
        }

        .email-address {
          font-size: 13px;
          color: #ddd;
          flex: 1;
        }

        .remove-btn {
          background: none;
          border: none;
          color: #ff6363;
          cursor: pointer;
          padding: 4px 8px;
          border-radius: 4px;
          transition: background-color 0.2s, color 0.2s;
          display: flex;
          align-items: center;
          gap: 4px;
          font-size: 14px;
        }

        .remove-btn:hover {
          background: #3a1f1f;
          color: #ff8888;
        }

        .add-email-form {
          display: flex;
          gap: 8px;
        }

        .email-input {
          flex: 1;
          background: #202020;
          border: 1px solid #444;
          border-radius: 6px;
          padding: 10px 12px;
          font-size: 13px;
          color: #ddd;
          font-family: inherit;
        }

        .email-input:focus {
          outline: 1px solid #4aa3ff;
          border-color: #4aa3ff;
        }

        .add-btn {
          padding: 10px 16px;
          background: #28a745;
          color: white;
          border: none;
          border-radius: 6px;
          cursor: pointer;
          font-size: 13px;
          font-weight: 500;
          display: flex;
          align-items: center;
          gap: 6px;
          transition: background-color 0.2s;
        }

        .add-btn:hover {
          background: #218838;
        }

        .btn-primary {
          padding: 10px 20px;
          background: linear-gradient(135deg, #ff7e00, #ffae42);
          color: #111;
          border: none;
          border-radius: 8px;
          cursor: pointer;
          font-size: 14px;
          font-weight: 600;
          display: flex;
          align-items: center;
          gap: 6px;
          transition: filter 0.2s;
        }

        .btn-primary:hover:not(:disabled) {
          filter: brightness(1.1);
        }

        .btn-primary:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .btn-secondary {
          padding: 10px 20px;
          background: #2f2f2f;
          color: #ddd;
          border: 1px solid #444;
          border-radius: 8px;
          cursor: pointer;
          font-size: 14px;
          font-weight: 500;
          transition: background-color 0.2s;
        }

        .btn-secondary:hover {
          background: #393939;
          color: #fff;
        }

        .btn-test {
          padding: 10px 20px;
          background: #17a2b8;
          color: white;
          border: none;
          border-radius: 8px;
          cursor: pointer;
          font-size: 14px;
          font-weight: 500;
         

        .btn-notification-test {
          padding: 10px 20px;
          background: #6f42c1;
          color: white;
          border: none;
          border-radius: 8px;
          cursor: pointer;
          font-size: 14px;
          font-weight: 500;
          display: flex;
          align-items: center;
          gap: 6px;
          transition: background-color 0.2s;
        }

        .btn-notification-test:hover:not(:disabled) {
          background: #5a32a3;
        }

        .btn-notification-test:disabled {
          background: #4a3960;
          cursor: not-allowed;
          opacity: 0.6;
        } display: flex;
          align-items: center;
          gap: 6px;
          transition: background-color 0.2s;
        }

        .btn-test:hover:not(:disabled) {
          background: #138496;
        }

        .btn-test:disabled {
          background: #4a5960;
          cursor: not-allowed;
          opacity: 0.6;
        }

        .error-message {
          background: #3a1f1f;
          border: 1px solid #5d2a2a;
          color: #ffb6b6;
          padding: 12px;
          border-radius: 8px;
          margin-top: 16px;
          font-size: 13px;
        }

        .success-message {
          background: #1f3a25;
          border: 1px solid #2a5d34;
          color: #a8ffb6;
          padding: 12px;
          border-radius: 8px;
          margin-top: 16px;
          font-size: 13px;
        }

        .spinner {
          display: inline-block;
          width: 32px;
          height: 32px;
          border: 3px solid rgba(255, 255, 255, 0.2);
          border-top-color: #4aa3ff;
          border-radius: 50%;
          animation: spin 0.8s linear infinite;
        }

        .spinner-small {
          display: inline-block;
          width: 14px;
          height: 14px;
          border: 2px solid rgba(255, 255, 255, 0.3);
          border-top-color: white;
          border-radius: 50%;
          animation: spin 0.8s linear infinite;
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};
